import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useRoles } from '../hooks/useRoles';

export interface UserProfileProps {
  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Show user avatar/icon
   * @default true
   */
  showAvatar?: boolean;

  /**
   * Show user email
   * @default true
   */
  showEmail?: boolean;

  /**
   * Show user roles
   * @default false
   */
  showRoles?: boolean;

  /**
   * Avatar size in pixels
   * @default 40
   */
  avatarSize?: number;

  /**
   * Custom inline styles
   */
  style?: React.CSSProperties;

  /**
   * Callback when profile is clicked
   */
  onClick?: () => void;
}

/**
 * Pre-built user profile display component
 *
 * Displays the current user's information including name, email, and optionally roles.
 * Automatically hides when user is not authenticated.
 *
 * @example Basic usage
 * ```tsx
 * import { UserProfile } from 'antaeus.keycloak.react';
 *
 * function Header() {
 *   return <UserProfile />;
 * }
 * ```
 *
 * @example With roles
 * ```tsx
 * <UserProfile showRoles={true} />
 * ```
 *
 * @example Clickable profile
 * ```tsx
 * <UserProfile
 *   onClick={() => navigate('/profile')}
 *   style={{ cursor: 'pointer' }}
 * />
 * ```
 *
 * @example Customized
 * ```tsx
 * <UserProfile
 *   showEmail={false}
 *   showRoles={true}
 *   avatarSize={50}
 *   className="my-profile"
 * />
 * ```
 */
export const UserProfile: React.FC<UserProfileProps> = ({
  className = '',
  showAvatar = true,
  showEmail = true,
  showRoles = false,
  avatarSize = 40,
  style,
  onClick,
}) => {
  const auth = useAuth();
  const { roles } = useRoles();

  // Don't render if not authenticated
  if (!auth.isAuthenticated || !auth.user) {
    return null;
  }

  const profile = auth.user.profile;
  const name = profile.name || profile.preferred_username || 'User';
  const email = profile.email;
  const initials = getInitials(name);

  const baseClass = 'kc-user-profile';
  const clickableClass = onClick ? 'kc-user-profile--clickable' : '';
  const classes = `${baseClass} ${clickableClass} ${className}`.trim();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      className={classes}
      style={style}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyPress={onClick ? (e) => e.key === 'Enter' && handleClick() : undefined}
    >
      {showAvatar && (
        <div
          className="kc-user-profile__avatar"
          style={{
            width: avatarSize,
            height: avatarSize,
            fontSize: avatarSize * 0.4,
          }}
        >
          {initials}
        </div>
      )}

      <div className="kc-user-profile__info">
        <div className="kc-user-profile__name">{name}</div>

        {showEmail && email && (
          <div className="kc-user-profile__email">{email}</div>
        )}

        {showRoles && roles.length > 0 && (
          <div className="kc-user-profile__roles">
            {roles.slice(0, 3).map((role) => (
              <span key={role} className="kc-user-profile__role-badge">
                {role}
              </span>
            ))}
            {roles.length > 3 && (
              <span className="kc-user-profile__role-badge">
                +{roles.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Helper function to get user initials from name
 */
function getInitials(name: string): string {
  const parts = name.trim().split(' ');

  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
